import { ButtonHierarchy, ButtonSize, MainButton } from "@beydesign/storybook"
import { Box, useMediaQuery, useTheme } from "@mui/material"
import React, { useState } from "react"
import { useAppContext } from "../../app/Context"
import { useDeviceAV } from "../../app/hooks/useDeviceAV"
import { ChatWithAgent } from "../CustomVideoConference/ChatWithAgent"
import { DeviceSelectionDialog } from "../MediaControlsBar/DeviceSelectionDialog"
import { MediaControlsBar } from "../MediaControlsBar/MediaControlsBar"
import { UserVideo } from "../UserVideo/UserVideo"

interface JoinMeetingModalProps {
  open: boolean
  onClose: () => void
  onJoinCall: () => void
}

export const JoinMeetingModal: React.FC<JoinMeetingModalProps> = ({
  open,
  onClose,
  onJoinCall,
}) => {
  const context = useAppContext()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const [isJoining, setIsJoining] = useState(false)

  const {
    audioInputs,
    videoInputs,
    isMicEnabled,
    isCameraEnabled,
    settingsOpen,
    selectedAudioInput,
    selectedVideoInput,
    permissionsGranted,
    handleAudioInputChange,
    handleVideoInputChange,
    handleSettingsClick,
    handleSettingsClose,
    toggleMicrophone,
    toggleCamera,
  } = useDeviceAV()

  if (!open) return null

  const positionWeb = `var(--spacing-tokens-size-in-px-spacing-spacing-xl)`
  const positionMobile = `var(--spacing-tokens-size-in-px-spacing-spacing-9xl)`

  const handleJoinClick = async () => {
    if (isJoining) return
    setIsJoining(true)
    try {
      await onJoinCall()
    } finally {
      setIsJoining(false)
    }
  }

  return (
    <Box
      position="absolute"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      width="100%"
      height="100%"
      top={0}
      left={0}
      zIndex={10}
      sx={{
        backdropFilter: "blur(10px)",
        backgroundColor: "rgba(0, 0, 0, 0.15)",
        boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
        border: "1px solid rgba(255, 255, 255, 0.18)",
        borderRadius: "1rem",
      }}
      onClick={onClose}
    >
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        position="relative"
        width="100%"
        height="100%"
        onClick={(e) => e.stopPropagation()}
      >
        <MainButton
          onClick={handleJoinClick}
          hierarchy={ButtonHierarchy.PrimaryLemon}
          size={ButtonSize.xl}
          text={isJoining ? "Joining..." : "Start Conversation"}
          disabled={isJoining}
        />
        <Box
          position="absolute"
          bottom={isMobile ? positionMobile : positionWeb}
          left={!isMobile && positionWeb}
          right={isMobile ? "5%" : undefined}
          width="25%"
          height="auto"
          borderRadius={"var(--spacing-tokens-size-in-px-spacing-spacing-xl)"}
        >
          <UserVideo />
        </Box>

        <MediaControlsBar
          isMicEnabled={isMicEnabled}
          isCameraEnabled={isCameraEnabled}
          toggleMicrophone={toggleMicrophone}
          toggleCamera={toggleCamera}
          onSettingsClick={handleSettingsClick}
          bottom={isMobile ? "2%" : positionWeb}
        />
      </Box>

      <DeviceSelectionDialog
        open={settingsOpen}
        onClose={handleSettingsClose}
        audioInputs={audioInputs}
        videoInputs={videoInputs}
        selectedAudioInput={selectedAudioInput}
        selectedVideoInput={selectedVideoInput}
        handleAudioInputChange={handleAudioInputChange}
        handleVideoInputChange={handleVideoInputChange}
      />
      <ChatWithAgent
        agent={context.agent}
        sx={{ top: "1rem", left: "1rem" }}
        onBackButtonClick={onClose}
      />
    </Box>
  )
}
